import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Phone, Mail, MapPin, Clock, CheckCircle2 } from 'lucide-react';
import { schoolInfo } from '../utils/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!formData.mobile.trim()) {
      toast.error('Please enter your mobile number');
      return;
    }
    if (formData.mobile.length !== 10 || !/^\d{10}$/.test(formData.mobile)) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }
    if (!formData.email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    if (!formData.subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    if (!formData.message.trim()) {
      toast.error('Please enter your message');
      return;
    }
    
    setSubmitting(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, {
        name: formData.name,
        mobile_number: formData.mobile,
        email: formData.email,
        subject: formData.subject,
        message: formData.message
      });
      
      setSubmitted(true);
      toast.success('Your message has been sent successfully!', {
        description: 'We will contact you soon.'
      });
      
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          mobile: '',
          subject: '',
          message: ''
        });
        setSubmitted(false);
      }, 3000);
      
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message', {
        description: 'Please try again or contact us directly.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <div className="bg-[#4A90E2] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            Contact Us
          </h1>
          <p className="text-xl text-gray-100">
            We're here to help. Get in touch with us.
          </p>
        </div>
      </div>

      {/* Contact Information & Form */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Info Cards */}
            <div className="space-y-6">
              <Card className="shadow-lg">
                <CardHeader>
                  <div className="w-12 h-12 bg-[#4A90E2]/10 rounded-full flex items-center justify-center mb-2">
                    <Phone className="h-6 w-6 text-[#4A90E2]" />
                  </div>
                  <CardTitle>Phone</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{schoolInfo.phone}</p>
                  <p className="text-sm text-gray-500 mt-2">Mon-Sat: 8:00 AM - 4:00 PM</p>
                </CardContent>
              </Card>

              <Card className="shadow-lg hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-[#4A90E2]/10 rounded-full flex items-center justify-center mb-2">
                    <Mail className="h-6 w-6 text-[#4A90E2]" />
                  </div>
                  <CardTitle>Email</CardTitle>
                </CardHeader>
                <CardContent>
                  <a 
                    href={`mailto:${schoolInfo.email}?subject=Inquiry from AMTEE Website`}
                    className="text-[#4A90E2] hover:text-[#357ABD] hover:underline font-medium break-all transition-colors"
                    aria-label="Send email to AMTEE PUBLIC SCHOOL"
                  >
                    {schoolInfo.email}
                  </a>
                  <p className="text-sm text-gray-500 mt-2">We'll respond within 24 hours</p>
                </CardContent>
              </Card>

              <Card className="shadow-lg">
                <CardHeader>
                  <div className="w-12 h-12 bg-[#4A90E2]/10 rounded-full flex items-center justify-center mb-2">
                    <MapPin className="h-6 w-6 text-[#4A90E2]" />
                  </div>
                  <CardTitle>Address</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{schoolInfo.address}</p>
                  <p className="text-sm text-gray-500 mt-2">Bahraich, Uttar Pradesh</p>
                </CardContent>
              </Card>

              <Card className="shadow-lg">
                <CardHeader>
                  <div className="w-12 h-12 bg-[#4A90E2]/10 rounded-full flex items-center justify-center mb-2">
                    <Clock className="h-6 w-6 text-[#4A90E2]" />
                  </div>
                  <CardTitle>Office Hours</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">Monday - Friday</p>
                  <p className="text-sm text-gray-500">8:00 AM - 4:00 PM</p>
                  <p className="text-gray-600 mt-2">Saturday</p>
                  <p className="text-sm text-gray-500">8:00 AM - 1:00 PM</p>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="shadow-xl">
                <CardHeader>
                  <CardTitle className="text-2xl">Send us a Message</CardTitle>
                </CardHeader>
                <CardContent>
                  {submitted ? (
                    <Alert className="bg-green-50 border-green-200">
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      <AlertDescription className="text-green-800 font-medium">
                        Thank you for contacting us! We'll get back to you within 24 hours.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="name">Your Name *</Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder="Enter your name"
                          />
                        </div>
                        <div>
                          <Label htmlFor="mobile">Mobile Number *</Label>
                          <Input
                            id="mobile"
                            name="mobile"
                            type="tel"
                            value={formData.mobile}
                            onChange={handleChange}
                            required
                            placeholder="10-digit mobile number"
                            pattern="[0-9]{10}"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="your.email@example.com"
                        />
                      </div>

                      <div>
                        <Label htmlFor="subject">Subject *</Label>
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                          placeholder="What is this regarding?"
                        />
                      </div>

                      <div>
                        <Label htmlFor="message">Message *</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          placeholder="Write your message here..."
                          rows={6}
                        />
                      </div>

                      <Button 
                        type="submit" 
                        className="w-full bg-[#4A90E2] hover:bg-[#357ABD] text-lg py-6"
                        disabled={submitting}
                      >
                        {submitting ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                            Sending...
                          </>
                        ) : (
                          'Send Message'
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
